import type { Monaco } from '@monaco-editor/react';

import { ArticleSuggestion, getArticleSuggestions } from '../embeds/articleSuggestStore';
import { getCachedLinkPath, getLinkFiles, resolveLinkPath } from '../embeds/linkFileStore';
import { COMPONENT_DEFS, INLINE_DEFS, getComponentDef, getInlineDef } from '../embeds/registry';
import { resolveBlockAt } from '../parser';

/** Monaco の language ID */
export const KNZ_LANGUAGE_ID = 'knz';

/**
 * frontmatter の param 定義（補完の single source of truth）。
 * 記法・仕様は tech_knz.md の frontmatter 仕様に対応する。
 * - title / author は必須。author はリスト形式（`- userId`）にも対応するため補完では `author:\n - ` を挿入する。
 * - visible は記事機能の公開設定（ArticleVisibility）に対応。
 * - prose は読み物モード切替。
 * sampleValues を持つ param は値補完（`key:` の後）を出す。
 */
type FrontmatterParamDef = {
  key: string;
  required: boolean;
  description: string;
  /** 値候補（`key:` の後で補完する）。無ければ値補完なし */
  sampleValues?: string[];
  /** 挿入テキスト（省略時は `key: `）。author のようにリストを続ける場合に使う */
  insertText?: string;
};

const FRONTMATTER_PARAMS: FrontmatterParamDef[] = [
  { key: 'title', required: true, description: '記事タイトル（必須）' },
  {
    key: 'author',
    required: true,
    description: '著者の userId（必須・1件以上）。`author:` の下に `- userId` を並べる',
    insertText: 'author:\n - ',
  },
  { key: 'subtitle', required: false, description: '副題（記事ヘッダーのタイトル下に表示）' },
  {
    key: 'description',
    required: false,
    description: '記事の説明（一覧カード・記事フォームで使用）',
  },
  {
    key: 'visible',
    required: false,
    description: '公開設定（記事機能の ArticleVisibility に対応）',
    sampleValues: ['PUBLIC', 'INTERNAL', 'PRIVATE'],
  },
  {
    key: 'prose',
    required: false,
    description: '読み物モード（明朝の紙面で描画）。true で有効化',
    sampleValues: ['true'],
  },
  { key: 'breadcrumb', required: false, description: '読み物モードのパンくず親ラベル' },
];

/** mention 補完用のユーザー一覧（userId / 表示名）。エディタ側から setMentionUsers で更新する */
type MentionUser = { userId: string; userName: string };
let mentionUsers: MentionUser[] = [];

/**
 * `@` / `{{mention:` の補完に使うユーザー一覧を設定する。
 * 補完プロバイダはこの参照を読むため、KnzEditor 側で最新の users を渡す。
 */
export const setMentionUsers = (users: MentionUser[]): void => {
  mentionUsers = users;
};

/**
 * 登録済みの補完プロバイダ。再登録時に dispose して二重登録（＝候補の重複表示）を防ぐ。
 * Monaco はグローバルシングルトンのため、言語 ID 単位で 1 つだけ保持すればよい。
 */
let completionDisposable: { dispose: () => void } | null = null;

/** 登録済みの定義プロバイダ。補完と同様、再登録時に dispose して二重登録を防ぐ。 */
let definitionDisposable: { dispose: () => void } | null = null;

/** ホバー・アウトライン・折りたたみ・参照の各プロバイダ。再登録時に dispose する。 */
let hoverDisposable: { dispose: () => void } | null = null;
let symbolDisposable: { dispose: () => void } | null = null;
let foldingDisposable: { dispose: () => void } | null = null;
let referenceDisposable: { dispose: () => void } | null = null;

/**
 * 指定行（0-indexed）が `link:<fileId>` 行なら、その fileId を返す。
 * link 定義ジャンプ（別ファイルを開く）の対象判定に使う。CodeBlock ブロック内に限らず
 * `link:` 行そのものを対象にする（value を trim して返す）。空なら undefined。
 */
/**
 * 指定行（0-indexed）が frontmatter ブロック内（先頭 `---` 〜 閉じ `---` の間）にあるか判定する。
 * 先頭が `---` でない場合は frontmatter 無しとみなし常に false。
 * 開き `---`・閉じ `---` の行自体は false（キー補完を出さない）。
 */
const isInFrontmatter = (source: string, lineIndex: number): boolean => {
  const lines = source.split('\n');
  if (lines[0]?.trim() !== '---') return false;
  // 閉じ `---` を探す
  let closeIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '---') {
      closeIndex = i;
      break;
    }
  }
  // 閉じが無ければ 1 行目以降すべてを frontmatter 途中とみなす（編集中）
  const upper = closeIndex === -1 ? lines.length : closeIndex;
  return lineIndex >= 1 && lineIndex < upper;
};

export const resolveLinkFileIdAt = (source: string, lineIndex: number): string | undefined => {
  const lines = source.split('\n');
  const line = lines[lineIndex];
  if (line === undefined) return undefined;
  const m = line.match(/^\s*link:(.+)$/);
  if (!m) return undefined;
  const fileId = m[1].trim();
  return fileId === '' ? undefined : fileId;
};

/**
 * Monaco に knz 言語・ハイライト・テーマ・補完を登録する。
 * `beforeMount(monaco)` から呼ぶ。
 * 言語・Monarch・テーマは重複しても同じ設定で上書きされるだけなので毎回呼んでよいが、
 * 補完プロバイダだけは積み重なると候補が重複するため、既存を dispose してから登録し直す。
 */
export const registerKnzLanguage = (monaco: Monaco): void => {
  // 言語が未登録なら登録する（言語だけ他所で登録済みでも補完は必ず登録する）
  const hasLanguage = monaco.languages.getLanguages().some((l) => l.id === KNZ_LANGUAGE_ID);
  if (!hasLanguage) {
    monaco.languages.register({ id: KNZ_LANGUAGE_ID, extensions: ['.knz'] });
  }

  // --- Monarch トークナイザ（ブロックタグ・key・インライントークン・frontmatter） ---
  monaco.languages.setMonarchTokensProvider(KNZ_LANGUAGE_ID, {
    tokenizer: {
      root: [
        // 行コメント（// で始まる行）
        [/^\s*\/\/.*$/, 'knz-comment'],
        // frontmatter 区切り
        [/^---$/, 'knz-fence'],
        // 閉じタグ <--- !Name --->
        [/^<---\s*!\w+\s*--->$/, 'knz-tag-close'],
        // 開始タグ <--- Name --->
        [/^<---\s*\w+\s*--->$/, 'knz-tag-open'],
        // key: value の key 部分
        [/^([A-Za-z][\w]*)(:)/, ['knz-key', 'knz-colon']],
        // リスト項目
        [/^\s+-.*$/, 'knz-list'],
        // インライントークン {{name:value}} … 開き括弧で @inline 状態へ入る
        [/\{\{/, { token: 'knz-inline-bracket', bracket: '@open', next: '@inline' }],
        // メンション @userId
        [/@[A-Za-z0-9._-]+/, 'knz-inline'],
      ],
      // {{ 〜 }} の内部。中身を色付けし、}} で root へ戻る。
      inline: [
        [/\}\}/, { token: 'knz-inline-bracket', bracket: '@close', next: '@pop' }],
        // 種別名 : 値
        [/[A-Za-z][\w]*(?=:)/, 'knz-inline-name'],
        [/:/, 'knz-colon'],
        [/[^}]+/, 'knz-inline'],
      ],
    },
    brackets: [{ open: '{{', close: '}}', token: 'knz-inline-bracket' }],
  });

  monaco.languages.setLanguageConfiguration(KNZ_LANGUAGE_ID, {
    comments: { lineComment: '//' },
    // `{{` `}}` を対応括弧として登録する（カーソルを合わせると対応括弧をハイライトする）。
    brackets: [['{{', '}}']],
    // 自動閉じは無効（空配列）にする。有効だと `{{` 入力時に `}}` が自動挿入され、
    // 補完 insertText の `}}` と重複して `}}}}` になる。閉じ括弧は補完側だけで完結させる。
    autoClosingPairs: [],
    surroundingPairs: [],
    // リスト継続（案A）: `- 項目` の行で Enter すると次行に同じインデント + `- ` を自動挿入する。
    // Markdown のリスト継続と同じ挙動。文脈（children/text）は見ず、`- ` を使う行全般に効く
    //（raw の CodeBlock/Mermaid では `- ` 記法を使わないため実害はない）。
    onEnterRules: [
      {
        // 内容のある項目 `- xxx`（`-` の後にスペース + 内容あり）→ 次行に `- ` を継ぐ。
        // 空項目（`- ` だけ）はこの規則にマッチしないため継続せず、素の改行になる
        //（＝空項目で Enter するとリスト継続から自然に抜ける）。
        beforeText: /^\s*-\s+\S.*$/,
        action: {
          indentAction: monaco.languages.IndentAction.None,
          appendText: '- ',
        },
      },
    ],
  });

  // --- テーマ（light / dark） ---
  const rules = [
    { token: 'knz-tag-open', foreground: '4fc1ff', fontStyle: 'bold' },
    { token: 'knz-tag-close', foreground: '4fc1ff' },
    { token: 'knz-key', foreground: 'c586c0' },
    { token: 'knz-colon', foreground: '808080' },
    { token: 'knz-list', foreground: 'ce9178' },
    { token: 'knz-inline', foreground: 'dcdcaa' },
    { token: 'knz-inline-bracket', foreground: 'ffd700', fontStyle: 'bold' },
    { token: 'knz-inline-name', foreground: '4ec9b0', fontStyle: 'bold' },
    { token: 'knz-fence', foreground: '569cd6' },
    { token: 'knz-comment', foreground: '6a9955', fontStyle: 'italic' },
  ];
  monaco.editor.defineTheme('knz-light', {
    base: 'vs',
    inherit: true,
    rules: rules.map((r) => ({ ...r })),
    colors: { 'minimap.dropShadow': '#00000000', 'scrollbar.shadow': '#00000000' },
  });
  monaco.editor.defineTheme('knz-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: rules.map((r) => ({ ...r })),
    colors: { 'minimap.dropShadow': '#00000000', 'scrollbar.shadow': '#00000000' },
  });

  registerCompletion(monaco);
  registerDefinition(monaco);
  registerHover(monaco);
  registerDocumentSymbol(monaco);
  registerFolding(monaco);
  registerReference(monaco);
};

/**
 * 定義ジャンプ（children id ⇄ 定義ブロック）プロバイダを登録する。
 * 既存があれば dispose してから登録し直す（二重登録防止）。
 *
 * 双方向に対応する:
 * - 順方向: children 配下のリスト行（`-id`）でジャンプすると、その id を持つブロックの
 *   開始行（`<--- Name --->`）へ移動する。
 * - 逆方向: ブロックの `id:<値>` 行でジャンプすると、その id を参照している children 行へ
 *   移動する。参照が複数ある場合は Location を配列で返し、Monaco が参照一覧（Peek）を出す。
 *
 * いずれも同一 model 内の Location を返すため、Monaco が自動でカーソルを移動する。
 *
 * link 参照（`link:<fileId>`）は別ファイルを開く必要があり、DefinitionProvider では
 * 完結しない。link は KnzEditor 側のキーバインドが onOpenLinkedFile 経由で処理する。
 */
const registerDefinition = (monaco: Monaco): void => {
  definitionDisposable?.dispose();
  definitionDisposable = monaco.languages.registerDefinitionProvider(KNZ_LANGUAGE_ID, {
    provideDefinition: (model, position) => {
      const source = model.getValue();
      const lineIndex = position.lineNumber - 1;

      /** 行番号（0-indexed）を、その行全体を指す Location に変換する */
      const lineLocation = (line: number) => ({
        uri: model.uri,
        range: {
          startLineNumber: line + 1,
          startColumn: 1,
          endLineNumber: line + 1,
          endColumn: model.getLineLength(line + 1) + 1,
        },
      });

      // --- 順方向: children の `-id` → 定義ブロック開始行 ---
      const childId = resolveChildIdAt(source, lineIndex);
      if (childId) {
        const defLine = findBlockLineById(source, childId);
        return defLine === undefined ? null : lineLocation(defLine);
      }

      // --- 逆方向: ブロックの `id:<値>` 行 → その id を参照している children 行（複数可） ---
      const definedId = resolveIdDefinitionAt(source, lineIndex);
      if (definedId) {
        const refLines = findChildRefLines(source, definedId);
        if (refLines.length === 0) return null;
        // 複数参照がある場合は配列で返す（Monaco が Peek で一覧表示する）
        return refLines.map(lineLocation);
      }

      return null;
    },
  });
};

/**
 * 指定行（0-indexed）がブロック内の `id:<値>` 行なら、その id を返す。
 * 逆方向ジャンプ（id → 参照している children 行）の起点判定に使う。
 * ブロック外の行や id 行でない場合は undefined。
 */
const resolveIdDefinitionAt = (source: string, lineIndex: number): string | undefined => {
  const lines = source.split('\n');
  const line = lines[lineIndex];
  if (line === undefined) return undefined;
  const idMatch = line.match(/^id:(.+)$/);
  if (!idMatch) return undefined;
  const id = idMatch[1].trim();
  return id === '' ? undefined : id;
};

/**
 * 指定 id を `-id` として参照している children リスト行の行番号（0-indexed）を全て返す。
 * children param に属するリスト行だけを対象にする（他の array 系リストは除外）。
 * 参照が無ければ空配列。
 */
const findChildRefLines = (source: string, id: string): number[] => {
  const lines = source.split('\n');
  const result: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    const listMatch = lines[i].match(/^\s*-\s*([\w.-]+)\s*$/);
    if (!listMatch || listMatch[1] !== id) continue;
    if (listBelongsToKey(source, i) === 'children') result.push(i);
  }
  return result;
};

/**
 * 指定行（0-indexed）が children 配下のリスト行（`-id`）なら、その id を返す。
 * children param に属するリスト項目のときだけ対象にする（他の array 系リストは除外）。
 * 対象外なら undefined。
 */
const resolveChildIdAt = (source: string, lineIndex: number): string | undefined => {
  const lines = source.split('\n');
  const line = lines[lineIndex];
  if (line === undefined) return undefined;
  const listMatch = line.match(/^\s*-\s*([\w.-]+)\s*$/);
  if (!listMatch) return undefined;
  // このリスト行が children param に属しているか確認する
  if (listBelongsToKey(source, lineIndex) !== 'children') return undefined;
  return listMatch[1];
};

/**
 * 指定 id を持つブロックの開始行（`<--- Name --->`）の行番号（0-indexed）を返す。
 * 見つからなければ undefined。
 */
const findBlockLineById = (source: string, id: string): number | undefined => {
  const lines = source.split('\n');
  const OPEN_RE = /^<---\s*\w+\s*--->$/;
  const CLOSE_RE = /^<---\s*!\w+\s*--->$/;
  let openLine = -1;
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (OPEN_RE.test(t)) {
      openLine = i;
      continue;
    }
    if (CLOSE_RE.test(t)) {
      openLine = -1;
      continue;
    }
    if (openLine >= 0) {
      const idM = t.match(/^id:(.+)$/);
      if (idM && idM[1].trim() === id) return openLine;
    }
  }
  return undefined;
};

/**
 * Monaco の DocumentSymbol 型（アウトライン用）。
 * monaco-editor を直接 import せず、`Monaco` 型（`typeof monaco`）の
 * registerDocumentSymbolProvider のシグネチャから DocumentSymbol の要素型を取り出す。
 */
type SymbolProvider = Parameters<Monaco['languages']['registerDocumentSymbolProvider']>[1];
type ProvideSymbolsReturn = ReturnType<SymbolProvider['provideDocumentSymbols']>;
/** ProviderResult<DocumentSymbol[]> から DocumentSymbol 単体を取り出す */
type KnzDocumentSymbol =
  NonNullable<Awaited<ProvideSymbolsReturn>> extends readonly (infer T)[] ? T : never;

/** Monaco の FoldingRange 型（折りたたみ用）。DocumentSymbol と同様に Monaco 型から導出する。 */
type FoldingProvider = Parameters<Monaco['languages']['registerFoldingRangeProvider']>[1];
type ProvideFoldingReturn = ReturnType<FoldingProvider['provideFoldingRanges']>;
type KnzFoldingRange =
  NonNullable<Awaited<ProvideFoldingReturn>> extends readonly (infer T)[] ? T : never;

/** ソースから抽出したブロックの構造情報（アウトライン・ホバー・折りたたみで共有） */
type BlockInfo = {
  /** ブロック名（`<--- Name --->` の Name） */
  name: string;
  /** ブロックの id（`id:` param。無ければ undefined） */
  id?: string;
  /** 見出しになる param（title または label。無ければ undefined） */
  heading?: string;
  /** children で参照している id の一覧（順方向のツリー化に使う） */
  childRefs: string[];
  /** 開始タグの行（0-indexed） */
  openLine: number;
  /** 閉じタグの行（0-indexed）。閉じ忘れなら undefined */
  closeLine?: number;
};

/**
 * ソース全体を走査してブロック情報の一覧を返す（純関数）。
 * アウトライン・折りたたみ・ホバーが共有する。frontmatter は含めない。
 */
const collectBlocks = (source: string): BlockInfo[] => {
  const lines = source.split('\n');
  const OPEN_RE = /^<---\s*(\w+)\s*--->$/;
  const CLOSE_RE = /^<---\s*!\w+\s*--->$/;
  const blocks: BlockInfo[] = [];
  let current: BlockInfo | null = null;
  let inChildren = false;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const t = raw.trim();
    const open = t.match(OPEN_RE);
    if (open) {
      current = { name: open[1], childRefs: [], openLine: i };
      inChildren = false;
      continue;
    }
    if (CLOSE_RE.test(t)) {
      if (current) {
        current.closeLine = i;
        blocks.push(current);
      }
      current = null;
      inChildren = false;
      continue;
    }
    if (!current) continue;
    // children リスト行の収集（key 行の追跡で children ブロックか判定）
    const listItem = raw.match(/^\s*-\s*([\w.-]+)\s*$/);
    if (listItem) {
      if (inChildren) current.childRefs.push(listItem[1]);
      continue;
    }
    const kv = raw.match(/^([A-Za-z][\w]*):(.*)$/);
    if (kv) {
      const key = kv[1];
      const value = kv[2].trim();
      inChildren = key === 'children';
      if (key === 'id') current.id = value;
      else if (key === 'title' || key === 'label') {
        // title を優先。label しか無ければ label を使う。
        if (key === 'title' || current.heading === undefined) current.heading = value;
      } else if (key === 'children' && value !== '') {
        // カンマ区切り children（非推奨だが読む）
        const refs = value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v !== '');
        current.childRefs.push(...refs);
      }
    }
  }
  return blocks;
};

/**
 * ArticleNav の articleId 補完候補を作る（`self:` / `articles:` 用）。
 * 候補は「自分が著者の記事のみ」（articleSuggestStore が authorUserIds で絞る）。
 * タイトルを主ラベル、articleId を補助表示にし、挿入されるのは articleId。
 *
 * insertPrefix を渡すと insertText の先頭に付与する（articles のリスト `- ` 形式を強制する用途）。
 * このとき range は `-` を含むため、filterText も `-` から始めてフィルタに通す。
 */
const articleSuggestions = (
  monaco: Monaco,
  suggestions: ArticleSuggestion[],
  range: {
    startLineNumber: number;
    endLineNumber: number;
    startColumn: number;
    endColumn: number;
  },
  insertPrefix = '',
) =>
  suggestions.map((a, i) => ({
    // タイトルを主ラベル、articleId を補助表示にする
    label: { label: a.title, description: a.articleId },
    kind: monaco.languages.CompletionItemKind.Reference,
    detail: a.subtitle,
    // 挿入されるのは articleId
    insertText: `${insertPrefix}${a.articleId}`,
    // タイトル・articleId の両方で絞り込めるようにする（prefix 使用時は `-` 始まりに合わせる）
    filterText: `${insertPrefix ? '-' : ''}${a.title} ${a.articleId}`,
    sortText: String(i).padStart(4, '0'),
    range,
  }));

/** 補完プロバイダを登録する（registry から自動生成）。既存があれば dispose してから登録する。 */
const registerCompletion = (monaco: Monaco): void => {
  // 二重登録による候補の重複を防ぐため、既存プロバイダを破棄する。
  completionDisposable?.dispose();
  completionDisposable = monaco.languages.registerCompletionItemProvider(KNZ_LANGUAGE_ID, {
    // 改行だけで補完が出るのを避けるため `\n` はトリガに含めない。
    // コンポーネント補完は `<`、値は `:`、インラインは `{` / `@`、
    // children のリスト項目は `-` で出す（いずれも手動 Ctrl+Space も可）。
    triggerCharacters: ['<', ':', '{', '@', '-'],
    provideCompletionItems: (model, position) => {
      const source = model.getValue();
      const lineContent = model.getLineContent(position.lineNumber);
      const textBefore = lineContent.slice(0, position.column - 1);
      const textAfter = lineContent.slice(position.column - 1);

      // カーソル直後に既に `}}`（自動閉じ由来など）があれば、その分を置換範囲に含めて消費する。
      // これで insertText 側の `}}` と重複して `}}}}` になるのを防ぐ。
      const trailingBraces = /^\s*\}\}/.exec(textAfter);
      const trailingBraceLen = trailingBraces ? trailingBraces[0].length : 0;

      // --- frontmatter 内の補完（param キー / 値） ---
      // frontmatter はブロック（`<--- --->`）ではないため、専用の補完を先に処理する。
      if (isInFrontmatter(source, position.lineNumber - 1)) {
        const fmRangeFrom = (startColumn: number) => ({
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn,
          endColumn: position.column,
        });

        // (fm-1) `key:` の後 → 値候補（sampleValues を持つ key のみ。例: visible / prose）
        const fmValueMatch = textBefore.match(/^([A-Za-z][\w]*):(\s*\S*)$/);
        if (fmValueMatch) {
          const paramDef = FRONTMATTER_PARAMS.find((p) => p.key === fmValueMatch[1]);
          if (paramDef?.sampleValues) {
            const valuePart = fmValueMatch[2] ?? '';
            // `key:` の後に続く空白＋入力中の値を置換範囲にする（先頭スペースは維持したいので値部分のみ）
            const trimmedLen = valuePart.replace(/^\s+/, '').length;
            const valueRange = fmRangeFrom(position.column - trimmedLen);
            return {
              suggestions: paramDef.sampleValues.map((v) => ({
                label: v,
                kind: monaco.languages.CompletionItemKind.Value,
                insertText: v,
                range: valueRange,
              })),
            };
          }
          // sampleValues を持たない key は値補完なし（何も出さない）
          return { suggestions: [] };
        }

        // (fm-2) 行頭（入力中のキー名のみ）→ frontmatter param キー候補
        const fmKeyTyping = textBefore.match(/^([A-Za-z]*)$/);
        if (fmKeyTyping) {
          const keyRange = fmRangeFrom(position.column - fmKeyTyping[1].length);
          return {
            suggestions: FRONTMATTER_PARAMS.map((p) => ({
              label: p.key,
              kind: monaco.languages.CompletionItemKind.Property,
              detail: p.required ? '必須' : '任意',
              documentation: p.description,
              insertText: p.insertText ?? `${p.key}: `,
              // 必須を上に、その後は定義順で安定表示
              sortText: `${p.required ? '0' : '1'}_${p.key}`,
              range: keyRange,
            })),
          };
        }

        // frontmatter 内のそれ以外（リスト行 `- id` 等）は補完なし
        return { suggestions: [] };
      }

      // カーソルが属するブロックを先に解決する（ブロック内かどうかで補完を出し分けるため）
      const blockName = resolveBlockAt(source, position.lineNumber - 1);
      const def = blockName ? getComponentDef(blockName) : undefined;

      // 現在行が開始/閉じタグ行そのものか（タグ行ではブロック挿入を優先する）
      const isTagLine = /^\s*<---/.test(lineContent);

      // (a) ブロックの外（または `<` 入力中）→ コンポーネントひな形
      // ブロック内（def あり）かつタグ行でないときは param 補完へ回す。
      const inBlockBody = def !== undefined && !isTagLine;
      if (!inBlockBody && /^\s*<?$/.test(textBefore)) {
        // 既に入力済みの行頭の空白＋`<` を置換範囲に含める（`<<---` の二重化を防ぐ）。
        // 行の先頭（1）から現在位置までを置換対象にする。
        const blockRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: 1,
          endColumn: position.column,
        };
        // range が `<` を含むと、Monaco は range 内テキスト `<` で候補をフィルタする。
        // 候補ラベルは `Callout` 等で `<` を含まないため、filterText に `<` を付けて
        // フィルタに通るようにする（range で二重化を防ぎつつ表示も維持する）。
        const suggestions = COMPONENT_DEFS.map((def) => ({
          label: def.name,
          kind: monaco.languages.CompletionItemKind.Class,
          detail: def.label,
          documentation: def.description,
          insertText: def.template,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          filterText: `<${def.name}`,
          range: blockRange,
        }));
        return { suggestions };
      }

      /** 現在位置の指定列から現在位置までを置換範囲にする（既入力分の重複を防ぐ） */
      const rangeFrom = (startColumn: number) => ({
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn,
        endColumn: position.column,
      });

      /**
       * 指定列〜（現在位置＋直後の `}}`）までを置換範囲にする。
       * insertText が `}}` で終わるインライン補完で使い、既存の `}}` を消費して二重化を防ぐ。
       */
      const rangeFromConsumingBraces = (startColumn: number) => ({
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn,
        endColumn: position.column + trailingBraceLen,
      });

      // (c) `{{` → インライン種別
      // 直近の `{{` の位置を求め、`{{`＋入力中の種別名を丸ごと置換して二重化を防ぐ。
      const inlineMatch = textBefore.match(/\{\{\s*[A-Za-z]*$/);
      if (inlineMatch) {
        const braceStart = position.column - inlineMatch[0].length; // `{{` の開始列（1-indexed）
        const inlineRange = rangeFromConsumingBraces(braceStart);
        const suggestions = INLINE_DEFS.map((def) => ({
          label: def.name,
          kind: monaco.languages.CompletionItemKind.Snippet,
          detail: def.label,
          documentation: `${def.description}\n\n${def.valueDescription}`,
          insertText: `{{${def.name}:$1}}`,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          // range が `{{` を含むため、filterText も `{{` から始める
          filterText: `{{${def.name}`,
          range: inlineRange,
        }));
        return { suggestions };
      }

      /**
       * ユーザー候補を作る（表示名を主ラベル、userId を補助表示）。
       * 置換範囲には接頭辞（`@` / `{{mention:`）を含めず「入力中の名前部分」だけを対象にする。
       * こうすると Monaco が照合する語は素の入力文字（空なら全件）になり、
       * filterText（userId + 表示名）と素直にマッチして全ユーザーが表示される。
       *
       * @param replaceRange 入力中の名前部分だけを置換する範囲
       * @param trailingText userId の後ろに付ける文字（`{{mention:` の場合の `}}` 等）
       */
      const userSuggestions = (
        replaceRange: {
          startLineNumber: number;
          endLineNumber: number;
          startColumn: number;
          endColumn: number;
        },
        trailingText: string,
      ) =>
        mentionUsers.map((u, i) => ({
          // 表示名を主ラベル、userId を補助表示にして見分けやすくする
          label: { label: u.userName, description: u.userId },
          kind: monaco.languages.CompletionItemKind.User,
          detail: u.userName,
          insertText: `${u.userId}${trailingText}`,
          // userId でも表示名でも絞り込めるよう両方を含める
          filterText: `${u.userId} ${u.userName}`,
          // 元の並び順を維持する（インデックスをゼロ埋めして安定ソート）
          sortText: String(i).padStart(4, '0'),
          range: replaceRange,
        }));

      // (d) `{{mention:` の後 → ユーザー候補。`{{mention:` の後ろの名前部分だけを置換し、
      //     userId + 直後の `}}` を挿入する（既存の `}}` は消費する）。
      const mentionTokenMatch = textBefore.match(/\{\{\s*mention:\s*([\w.-]*)$/);
      if (mentionTokenMatch) {
        const typed = mentionTokenMatch[1] ?? ''; // 入力中の名前部分
        const valueStart = position.column - typed.length;
        return {
          suggestions: userSuggestions(
            {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: valueStart,
              endColumn: position.column + trailingBraceLen,
            },
            '}}',
          ),
        };
      }

      // (e) `@userId` 入力中 → ユーザー候補。`@` の後ろの名前部分だけを置換し userId を挿入する。
      const atMatch = textBefore.match(/@([\w.-]*)$/);
      if (atMatch) {
        const typed = atMatch[1] ?? ''; // 入力中の名前部分（`@` は含めない）
        const valueStart = position.column - typed.length;
        return {
          suggestions: userSuggestions(rangeFrom(valueStart), ''),
        };
      }

      // (f) children ブロック内のリスト行（`- id`）→ 定義済み id 候補。
      // 現在行が `-` 始まりで、直近の key 行が `children:` のときに発火する。
      // `-` を打った瞬間（triggerCharacters に `-` を追加）や Ctrl+Space で出る。
      // 推奨形 `- id`（ハイフン + 半角スペース）で挿入するため、`-` とそれに続く
      // 空白・入力中 id をまとめて `- <id>` に置換する。
      const listItemTyping = textBefore.match(/^(\s*)-(\s*)([\w.-]*)$/);
      // children / expected（研修設問の Expected id リスト）のリスト行で id 補完を出す
      const listKey = listBelongsToKey(source, position.lineNumber - 1);
      if (def && listItemTyping && (listKey === 'children' || listKey === 'expected')) {
        const indent = listItemTyping[1] ?? '';
        // `-` の開始列（1-indexed）。行頭インデントの直後。
        const dashStart = indent.length + 1;
        const dashRange = rangeFrom(dashStart);
        // 自分自身の id は循環になるので候補から除外する
        const ownId = resolveBlockIdAt(source, position.lineNumber - 1);
        // insertText は `- <id>` にして推奨形を強制する
        return {
          suggestions: idSuggestions(monaco, source, dashRange, ownId, '- '),
        };
      }

      // ArticleNav の `articles:` 配下のリスト行 → articleId 候補（自分が著者の記事のみ・非同期）。
      if (def?.name === 'ArticleNav' && listItemTyping && listKey === 'articles') {
        const indent = listItemTyping[1] ?? '';
        const dashStart = indent.length + 1;
        const dashRange = rangeFrom(dashStart);
        // insertText は `- <articleId>` にして推奨形を強制する
        return getArticleSuggestions().then((arts) => ({
          suggestions: articleSuggestions(monaco, arts, dashRange, '- '),
        }));
      }

      // (b') `key:` の後 → 値候補（sampleValues）
      // `key:` に続けて入力済みの値部分だけを置換する（`key:` 自体は残す）。
      const keyMatch = textBefore.match(/^([A-Za-z][\w]*):(\s*\S*)$/);
      if (keyMatch && def) {
        const valuePart = keyMatch[2] ?? '';
        const valueRange = rangeFrom(position.column - valuePart.length);
        const paramDef = def.params.find((p) => p.key === keyMatch[1]);
        if (paramDef?.sampleValues) {
          return {
            suggestions: paramDef.sampleValues.map((v) => ({
              label: v,
              kind: monaco.languages.CompletionItemKind.Value,
              insertText: v,
              range: valueRange,
            })),
          };
        }
        // children / expected の後は定義済み id
        if (keyMatch[1] === 'children' || keyMatch[1] === 'expected') {
          return { suggestions: idSuggestions(monaco, source, valueRange) };
        }
        // ArticleNav の self: / ArticleLinkCard の articleId: の後は
        // 自分が著者の記事の articleId（非同期）
        if (
          (def.name === 'ArticleNav' && keyMatch[1] === 'self') ||
          (def.name === 'ArticleLinkCard' && keyMatch[1] === 'articleId')
        ) {
          return getArticleSuggestions().then((arts) => ({
            suggestions: articleSuggestions(monaco, arts, valueRange),
          }));
        }
        // link: の後はマイワークスペースの全ファイル（fileId を挿入）
        if (keyMatch[1] === 'link') {
          return {
            suggestions: getLinkFiles().map((f, i) => ({
              // ファイル名を主ラベル、ディレクトリパスを補助表示にする
              label: { label: f.name, description: f.dirPath },
              kind: monaco.languages.CompletionItemKind.File,
              detail: f.dirPath,
              // 挿入されるのは fileId（プレビューで getFileDetail に使う）
              insertText: f.fileId,
              // ファイル名・パスの両方で絞り込めるようにする
              filterText: `${f.name} ${f.dirPath}`,
              sortText: String(i).padStart(4, '0'),
              range: valueRange,
            })),
          };
        }
      }

      // (b) ブロック内の行頭 → param キー候補
      // 入力中のキー名（行頭の英字列）を丸ごと置換する。
      const keyTyping = textBefore.match(/^([A-Za-z]*)$/);
      if (def && keyTyping) {
        const keyRange = rangeFrom(position.column - keyTyping[1].length);
        const suggestions = def.params
          .filter((p) => p.key !== 'text' || def.textAs === undefined)
          .map((p) => ({
            label: p.key,
            kind: monaco.languages.CompletionItemKind.Property,
            detail: `${p.required ? '必須' : '任意'} / ${p.type}`,
            documentation: p.description,
            insertText: `${p.key}:`,
            sortText: p.required ? `1_${p.key}` : `2_${p.key}`,
            range: keyRange,
          }));
        // id は children 参照用の識別子。param 一覧の最上位に出す。
        suggestions.unshift({
          label: 'id',
          kind: monaco.languages.CompletionItemKind.Property,
          detail: '任意 / 識別子（children から参照）',
          documentation: 'このブロックを children から参照するための id',
          insertText: 'id:',
          sortText: '0_id',
          range: keyRange,
        });
        // text は特別扱い（改行付き）
        if (def.textAs) {
          suggestions.push({
            label: 'text',
            kind: monaco.languages.CompletionItemKind.Property,
            detail: `本文 / ${def.textAs}`,
            documentation: '本文テキスト',
            insertText: def.textAs === 'raw' ? 'text:\n' : 'text:\n - ',
            sortText: '1_text',
            range: keyRange,
          });
        }
        return { suggestions };
      }

      return { suggestions: [] };
    },
  });
};

/**
 * 同一ソース内の定義済み id を補完候補にする（label=id, detail=name — title）。
 * excludeId を渡すと、その id を候補から除外する（自己参照＝循環を防ぐ）。
 *
 * insertPrefix を渡すと insertText の先頭に付与する（children の `- ` 形式を強制する用途）。
 * このとき range は `-` を含むため、filterText も `-`（+prefix 分）から始めてフィルタに通す。
 */
const idSuggestions = (
  monaco: Monaco,
  source: string,
  range: {
    startLineNumber: number;
    endLineNumber: number;
    startColumn: number;
    endColumn: number;
  },
  excludeId?: string,
  insertPrefix = '',
) => {
  const lines = source.split('\n');
  const results: { id: string; name: string; title: string }[] = [];
  let currentName: string | null = null;
  let currentTitle = '';
  let currentId = '';
  const OPEN_RE = /^<---\s*(\w+)\s*--->$/;
  const CLOSE_RE = /^<---\s*!\w+\s*--->$/;
  for (const raw of lines) {
    const t = raw.trim();
    const open = t.match(OPEN_RE);
    if (open) {
      currentName = open[1];
      currentTitle = '';
      currentId = '';
      continue;
    }
    if (CLOSE_RE.test(t)) {
      if (currentId && currentName) {
        results.push({ id: currentId, name: currentName, title: currentTitle });
      }
      currentName = null;
      continue;
    }
    if (currentName) {
      const idM = t.match(/^id:(.+)$/);
      if (idM) currentId = idM[1].trim();
      const titleM = t.match(/^(?:title|label):(.+)$/);
      if (titleM) currentTitle = titleM[1].trim();
    }
  }
  return results
    .filter((r) => r.id !== excludeId)
    .map((r) => ({
      label: r.id,
      kind: monaco.languages.CompletionItemKind.Reference,
      detail: r.title ? `${r.name} — ${r.title}` : r.name,
      insertText: `${insertPrefix}${r.id}`,
      // range に `-` を含む場合（insertPrefix 使用時）は、Monaco の照合語も `-` 始まりに
      // なるため、filterText を `-<prefix 相当>id` にしてフィルタへ通す。
      ...(insertPrefix ? { filterText: `-${r.id}` } : {}),
      range,
    }));
};

/**
 * 指定行（0-indexed）が属するブロック内で、直近上方の `key:` 行のキー名を返す。
 * リスト項目（`-item`）がどの param に属するかを判定するのに使う。
 * ブロック外、または key 行が見つからない場合は null。
 */
const listBelongsToKey = (source: string, lineIndex: number): string | null => {
  const lines = source.split('\n');
  const OPEN_RE = /^<---\s*\w+\s*--->$/;
  const CLOSE_RE = /^<---\s*!\w+\s*--->$/;
  for (let i = lineIndex - 1; i >= 0; i--) {
    const t = lines[i].trim();
    if (OPEN_RE.test(t)) return null; // ブロック先頭に達した（key 行なし）
    if (CLOSE_RE.test(t)) return null; // ブロック外
    // リスト項目行はスキップして遡る
    if (/^\s*-/.test(lines[i])) continue;
    const kv = lines[i].match(/^([A-Za-z][\w]*):/);
    if (kv) return kv[1];
    // それ以外の非空行は key の連なりが途切れたとみなす
    if (t !== '') return null;
  }
  return null;
};

/**
 * 指定行（0-indexed）が属するブロックの id を返す（自己参照除外用）。
 * ブロック内を上方向に探索し `id:` 行を拾う。無ければ undefined。
 */
const resolveBlockIdAt = (source: string, lineIndex: number): string | undefined => {
  const lines = source.split('\n');
  const OPEN_RE = /^<---\s*\w+\s*--->$/;
  const CLOSE_RE = /^<---\s*!\w+\s*--->$/;
  for (let i = lineIndex; i >= 0; i--) {
    const t = lines[i].trim();
    if (CLOSE_RE.test(t) && i !== lineIndex) return undefined; // 直前ブロックに入った
    if (OPEN_RE.test(t)) break; // 自ブロックの先頭
  }
  // 自ブロックの範囲内で id を探す
  let start = 0;
  for (let i = lineIndex; i >= 0; i--) {
    if (OPEN_RE.test(lines[i].trim())) {
      start = i;
      break;
    }
  }
  let end = lines.length;
  for (let i = lineIndex; i < lines.length; i++) {
    if (CLOSE_RE.test(lines[i].trim())) {
      end = i;
      break;
    }
  }
  for (let i = start + 1; i < end; i++) {
    const idM = lines[i].trim().match(/^id:(.+)$/);
    if (idM) return idM[1].trim();
  }
  return undefined;
};

// ============================================================================
// ホバー（HoverProvider）
// ============================================================================

/**
 * ホバープロバイダを登録する。カーソル位置の文脈に応じて registry・mention・
 * children 参照先の情報を Markdown で表示する。既存があれば dispose してから登録する。
 *
 * 表示対象:
 * - 開始/閉じタグの Name → registry の label・description・param 一覧
 * - ブロック内の param キー → その param の型・必須・説明
 * - `@userId` / `{{mention:userId}}` → ユーザー表示名
 * - children の `-id` → 参照先ブロックの name・見出し（title/label）
 */
const registerHover = (monaco: Monaco): void => {
  hoverDisposable?.dispose();
  hoverDisposable = monaco.languages.registerHoverProvider(KNZ_LANGUAGE_ID, {
    provideHover: async (model, position) => {
      const source = model.getValue();
      const lineIndex = position.lineNumber - 1;
      const lineContent = model.getLineContent(position.lineNumber);
      const wholeLineRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: 1,
        endColumn: lineContent.length + 1,
      };
      const md = (value: string) => ({
        range: wholeLineRange,
        contents: [{ value }],
      });

      // (1) 開始/閉じタグ → ブロック定義の説明
      const tagMatch = lineContent.trim().match(/^<---\s*!?(\w+)\s*--->$/);
      if (tagMatch) {
        const def = getComponentDef(tagMatch[1]);
        if (def) return md(blockHoverMarkdown(def.name));
        return null;
      }

      // (2) `@userId` にカーソル → mention 表示名
      //     カーソル列に重なる @token を探す
      const mentionAt = findTokenAt(lineContent, position.column, /@([\w.-]+)/g, 0);
      if (mentionAt) {
        const userId = mentionAt.replace(/^@/, '');
        return md(mentionHoverMarkdown(userId));
      }

      // (3) `{{name:value}}` にカーソル → インライン種別の説明（mention は表示名も）
      const inlineAt = findTokenAt(
        lineContent,
        position.column,
        /\{\{\s*([\w]+)\s*:\s*([^}]*)\}\}/g,
        0,
      );
      if (inlineAt) {
        const m = inlineAt.match(/\{\{\s*([\w]+)\s*:\s*([^}]*)\}\}/);
        if (m) {
          const name = m[1];
          const value = m[2].trim();
          if (name === 'mention') return md(mentionHoverMarkdown(value));
          const def = getInlineDef(name);
          if (def) {
            return md(
              [
                `**{{${def.name}}}** — ${def.label}`,
                '',
                def.description,
                '',
                `値: ${def.valueDescription}`,
              ].join('\n'),
            );
          }
        }
      }

      // (4) children の `-id` → 参照先ブロックの情報
      const childId = resolveChildIdAt(source, lineIndex);
      if (childId) {
        return md(idTargetHoverMarkdown(source, childId));
      }

      // (4.5) `link:<fileId>` 行 → 参照先ファイルの絶対パスをファイル取得API（API6106）で解決して表示。
      //       解決済みならキャッシュから即時、未解決なら await して取得する（取得中は「解決中」を出す）。
      const linkFileId = resolveLinkFileIdAt(source, lineIndex);
      if (linkFileId) {
        const cached = getCachedLinkPath(linkFileId);
        if (cached !== undefined) {
          // 解決済み（成功=パス / 失敗=null）
          return md(linkPathHoverMarkdown(cached));
        }
        // 未解決: API で解決してから表示（ホバーはこの Promise の解決を待って描画される）
        const resolved = await resolveLinkPath(linkFileId);
        return md(linkPathHoverMarkdown(resolved));
      }

      // (5) param キー行 → param の説明
      const keyMatch = lineContent.match(/^([A-Za-z][\w]*):/);
      if (keyMatch) {
        const blockName = resolveBlockAt(source, lineIndex);
        const def = blockName ? getComponentDef(blockName) : undefined;
        const key = keyMatch[1];
        if (key === 'id') {
          return md(
            ['**id** — 識別子', '', 'このブロックを children から参照するための id。'].join('\n'),
          );
        }
        const paramDef = def?.params.find((p) => p.key === key);
        if (paramDef) {
          return md(
            [
              `**${paramDef.key}** — ${paramDef.required ? '必須' : '任意'} / \`${paramDef.type}\``,
              '',
              paramDef.description,
              ...(paramDef.sampleValues ? ['', `例: ${paramDef.sampleValues.join(' / ')}`] : []),
            ].join('\n'),
          );
        }
      }

      return null;
    },
  });
};

/** ブロック定義（registry）を Markdown 文字列にする（ホバー表示用） */
const blockHoverMarkdown = (name: string): string => {
  const def = getComponentDef(name);
  if (!def) return `**${name}**`;
  const paramLines = def.params.map(
    (p) => `- \`${p.key}\`${p.required ? ' (必須)' : ''}: ${p.description}`,
  );
  return [
    `**<--- ${def.name} --->** — ${def.label}`,
    '',
    def.description,
    ...(paramLines.length > 0 ? ['', '**param:**', ...paramLines] : []),
  ].join('\n');
};

/**
 * `link:<fileId>` のホバー Markdown。
 * path が解決できていれば絶対パスを、null（不在・権限なし）ならその旨を出す。
 * fileId は内部値のためツールチップには含めない。
 */
const linkPathHoverMarkdown = (path: string | null): string => {
  if (path) {
    return [`**参照ファイル**`, '', `パス: \`${path}\``].join('\n');
  }
  return [
    `**参照ファイル**`,
    '',
    '（パスを取得できませんでした。ファイルが存在しないか、参照権限がありません）',
  ].join('\n');
};

/** mention（userId）のホバー Markdown。ユーザーが見つかれば表示名を出す。 */
const mentionHoverMarkdown = (userId: string): string => {
  const user = mentionUsers.find((u) => u.userId === userId);
  if (user) return [`**@${user.userName}**`, '', `userId: \`${userId}\``].join('\n');
  return [`**@${userId}**`, '', '（該当ユーザーが見つかりません）'].join('\n');
};

/** id 参照先ブロックのホバー Markdown（name と見出しを出す） */
const idTargetHoverMarkdown = (source: string, id: string): string => {
  const block = collectBlocks(source).find((b) => b.id === id);
  if (!block) return [`\`${id}\``, '', '（未定義の id。参照先が見つかりません）'].join('\n');
  const def = getComponentDef(block.name);
  const label = def ? def.label : block.name;
  return [
    `**${block.name}** — ${label}`,
    ...(block.heading ? ['', block.heading] : []),
    '',
    `id: \`${id}\``,
  ].join('\n');
};

/**
 * 行内で、指定列（1-indexed）に重なる正規表現トークンを返す。
 * `re` はグローバルフラグ付きの正規表現。マッチ全体（group 0）を返す。無ければ undefined。
 * `_group` は将来用の予約引数（現状は使わない）。
 */
const findTokenAt = (
  line: string,
  column: number,
  re: RegExp,
  _group: number,
): string | undefined => {
  const cursor = column - 1; // 0-indexed
  re.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    const start = m.index;
    const end = m.index + m[0].length;
    if (cursor >= start && cursor <= end) return m[0];
  }
  return undefined;
};

// ============================================================================
// アウトライン（DocumentSymbolProvider）
// ============================================================================

/**
 * アウトライン（ドキュメントシンボル）プロバイダを登録する。
 * ブロックを一覧・階層表示する。children 参照でツリー化し、
 * どこからも参照されないブロックをトップレベルに置く（描画の案C と同じ考え方）。
 */
const registerDocumentSymbol = (monaco: Monaco): void => {
  symbolDisposable?.dispose();
  symbolDisposable = monaco.languages.registerDocumentSymbolProvider(KNZ_LANGUAGE_ID, {
    provideDocumentSymbols: (model) => {
      const source = model.getValue();
      const blocks = collectBlocks(source);
      // id → BlockInfo の索引
      const byId = new Map<string, BlockInfo>();
      blocks.forEach((b) => {
        if (b.id) byId.set(b.id, b);
      });
      // 参照されている id の集合（トップレベル判定用）
      const referenced = new Set<string>();
      blocks.forEach((b) => b.childRefs.forEach((r) => referenced.add(r)));

      /** BlockInfo を DocumentSymbol に変換する（children を再帰。循環は visited で防ぐ） */
      const toSymbol = (b: BlockInfo, visited: Set<BlockInfo>): KnzDocumentSymbol => {
        const openLine = b.openLine + 1;
        const closeLine = (b.closeLine ?? b.openLine) + 1;
        const range = {
          startLineNumber: openLine,
          startColumn: 1,
          endLineNumber: closeLine,
          endColumn: model.getLineLength(closeLine) + 1,
        };
        const selectionRange = {
          startLineNumber: openLine,
          startColumn: 1,
          endLineNumber: openLine,
          endColumn: model.getLineLength(openLine) + 1,
        };
        const nextVisited = new Set(visited).add(b);
        const children = b.childRefs
          .map((r) => byId.get(r))
          .filter((c): c is BlockInfo => !!c && !nextVisited.has(c))
          .map((c) => toSymbol(c, nextVisited));
        return {
          name: b.heading ? `${b.name}: ${b.heading}` : b.name,
          detail: b.id ? `#${b.id}` : '',
          kind: symbolKindFor(monaco, b.name),
          tags: [],
          range,
          selectionRange,
          children,
        };
      };

      // トップレベル = どこからも children 参照されていないブロック（定義順）
      const roots = blocks.filter((b) => !b.id || !referenced.has(b.id));
      return roots.map((b) => toSymbol(b, new Set()));
    },
  });
};

/** ブロック名に応じた SymbolKind を返す（アウトラインのアイコン分け） */
const symbolKindFor = (monaco: Monaco, name: string) => {
  const K = monaco.languages.SymbolKind;
  switch (name) {
    case 'Section':
    case 'Chapter':
      return K.Namespace;
    case 'Toc':
      return K.Module;
    case 'CodeBlock':
    case 'Mermaid':
      return K.File;
    case 'Table':
    case 'ProseTable':
      return K.Struct;
    case 'List':
    case 'Checklist':
    case 'ProseList':
      return K.Array;
    case 'Tabs':
    case 'Tab':
    case 'Grid':
    case 'Card':
    case 'LinkCard':
    case 'ArticleLinkCard':
      return K.Class;
    default:
      return K.Field;
  }
};

// ============================================================================
// 折りたたみ（FoldingRangeProvider）
// ============================================================================

/**
 * 折りたたみプロバイダを登録する。
 * - 各ブロック（`<--- Name --->` 〜 `<--- !Name --->`）を折りたためる。
 * - 先頭 frontmatter（`---` 〜 `---`）を折りたためる。
 */
const registerFolding = (monaco: Monaco): void => {
  foldingDisposable?.dispose();
  foldingDisposable = monaco.languages.registerFoldingRangeProvider(KNZ_LANGUAGE_ID, {
    provideFoldingRanges: (model) => {
      const source = model.getValue();
      const ranges: KnzFoldingRange[] = [];

      // ブロック範囲
      collectBlocks(source).forEach((b) => {
        if (b.closeLine !== undefined && b.closeLine > b.openLine) {
          ranges.push({ start: b.openLine + 1, end: b.closeLine + 1 });
        }
      });

      // frontmatter（先頭 `---` 〜 次の `---`）
      const lines = source.split('\n');
      if (lines[0]?.trim() === '---') {
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim() === '---') {
            if (i > 1) {
              ranges.push({
                start: 1,
                end: i + 1,
                kind: monaco.languages.FoldingRangeKind.Region,
              });
            }
            break;
          }
        }
      }

      return ranges;
    },
  });
};

// ============================================================================
// 参照検索（ReferenceProvider）
// ============================================================================

/**
 * 参照検索（Shift+F12）プロバイダを登録する。定義ジャンプの逆引きロジックを再利用し、
 * id 定義行・children 参照行のどちらからでも「その id に関わる全 children 参照行」を返す。
 * includeDeclaration が true のときは定義ブロックの開始行も含める。
 */
const registerReference = (monaco: Monaco): void => {
  referenceDisposable?.dispose();
  referenceDisposable = monaco.languages.registerReferenceProvider(KNZ_LANGUAGE_ID, {
    provideReferences: (model, position, context) => {
      const source = model.getValue();
      const lineIndex = position.lineNumber - 1;

      // 対象 id を決める（children の `-id` 行 or ブロックの `id:` 行のどちらでも可）
      const id = resolveChildIdAt(source, lineIndex) ?? resolveIdDefinitionAt(source, lineIndex);
      if (!id) return [];

      const lineLocation = (line: number) => ({
        uri: model.uri,
        range: {
          startLineNumber: line + 1,
          startColumn: 1,
          endLineNumber: line + 1,
          endColumn: model.getLineLength(line + 1) + 1,
        },
      });

      const locations = findChildRefLines(source, id).map(lineLocation);
      // 宣言（定義ブロック開始行）を含める設定なら先頭に追加する
      if (context.includeDeclaration) {
        const defLine = findBlockLineById(source, id);
        if (defLine !== undefined) locations.unshift(lineLocation(defLine));
      }
      return locations;
    },
  });
};
